import {useAttendeeProfile} from 'Event/attendee-rules/AttendeeProfileProvider'
import {AND, OR, Rule} from 'Event/attendee-rules'
import {matchesTagsRule} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/TagsRule/matcher'
import {NESTED_RULE} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/NestedRule'
import {GROUP} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/GroupRule'
import {matchesGroupRule} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/GroupRule/matcher'
import {TAGS} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/TagsRule'
import {Groups, Tags} from 'Event/attendee'
import {FORM_RESPONSE} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/FormResponseRule'
import {matchesFormResponseRule} from './RuleConfig/RuleList/SingleRule/FormResponseRule/matcher'
import {Answer, useSubmissions} from 'Event/SubmissionsProvider'
import {Form} from 'organization/Event/FormsProvider'
import {useEvent} from 'Event/EventProvider'
import {TIME} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/TimeRule'
import {matchesTimeRule} from 'Event/attendee-rules/RuleConfig/RuleList/SingleRule/TimeRule/matcher'
import {useNow} from 'lib/date-time/DateProvider'
import {now as actualNow} from 'lib/date-time'

export const hasMatch = (
  {
    groups = {},
    tags = [],
    answers = [],
    forms = [],
  }: {groups?: Groups; tags?: Tags; answers?: Answer[]; forms?: Form[]},
  rules: Rule[],
  now: string = actualNow(),
): boolean =>
  rules.reduce((alreadyMatched: boolean, r: Rule, i: number) => {
    const isMatch = isTrue(groups, tags, answers, forms, r, now)
    switch (r.condition) {
      case AND:
        const isFirst = i === 0
        return isFirst ? isMatch : alreadyMatched && isMatch
      case OR:
        return alreadyMatched || isMatch
      default:
        throw new Error(`Unimplemented rule operator: ${r.condition}`)
    }
  }, false)

function isTrue(
  groups: Groups,
  tags: Tags,
  answers: Answer[],
  forms: Form[],
  r: Rule,
  now: string,
) {
  switch (r.source) {
    case NESTED_RULE:
      return hasMatch({groups, tags}, r.rules, now)
    case GROUP:
      return matchesGroupRule(groups, r)
    case TAGS:
      return matchesTagsRule(tags, r)
    case FORM_RESPONSE:
      return matchesFormResponseRule({answers, forms, rule: r})
    case TIME:
      return matchesTimeRule(r, now)
  }
}

/**
 * Check whether there is at least one visible item
 * @param resources
 * @returns
 */
export function useHasVisibleItems(items: {rules?: Rule[]}[]) {
  const {groups, tags} = useAttendeeProfile()
  const {answers} = useSubmissions()
  const {event} = useEvent()
  const {forms} = event
  const now = useNow()

  return (
    items.filter(({rules}) => {
      if (!rules || rules.length === 0) {
        return true
      }

      return hasMatch({groups, tags, answers, forms}, rules, now)
    }).length > 0
  )
}
